import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, profile, isAdmin, isWriter, isQA, isClient, signOut, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Redirect to login if not authenticated
  useEffect(() => {
    console.log('ProfilePage: Checking authentication');

    if (authLoading) {
      console.log('ProfilePage: Auth state is still loading');
      return; // Don't redirect while still loading
    }

    if (!user) {
      console.log('ProfilePage: No user found, redirecting to login');
      navigate('/login');
    } else {
      console.log('ProfilePage: User is authenticated');
    }
  }, [user, authLoading, navigate]);

  if (!user || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin mr-2">
          <Icon name="Loader2" size={24} />
        </div>
        <p>Loading profile...</p>
      </div>
    );
  }

  // Get user role as a string
  const getUserRole = () => {
    if (isAdmin()) return 'Administrator';
    if (isWriter()) return 'Writer';
    if (isQA()) return 'QA Reviewer';
    if (isClient()) return 'Client';
    return 'Regular User';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto glass-card p-8 rounded-2xl shadow-xl"
        >
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-text-primary">Your Profile</h1>
            <button
              onClick={() => navigate('/')}
              className="text-text-muted hover:text-text-primary flex items-center"
            >
              <Icon name="ArrowLeft" size={16} className="mr-1" />
              Back to Home
            </button>
          </div>

          {message && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                messageType === 'success'
                  ? 'bg-success/10 text-success border border-success/20'
                  : 'bg-error/10 text-error border border-error/20'
              }`}
            >
              {message}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="glass-card rounded-xl p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-text-primary">Account Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-text-muted mb-1">Email</label>
                    <p className="font-medium text-text-primary">{user.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-1">Full Name</label>
                    <p className="font-medium text-text-primary">{profile.full_name || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-1">Account Created</label>
                    <p className="font-medium text-text-primary">
                      {new Date(profile.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h2 className="text-xl font-semibold mb-4 text-text-primary">Account Actions</h2>
                <div className="space-y-3">
                  <button
                    onClick={signOut}
                    className="w-full py-2 bg-error/10 text-error border border-error/20 rounded-lg font-medium hover:bg-error/20 transition-colors flex items-center justify-center"
                  >
                    <Icon name="LogOut" size={16} className="mr-2" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>

            <div>
              <div className="glass-card rounded-xl p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-text-primary">Role & Permissions</h2>
                <div className="mb-4">
                  <label className="block text-sm text-text-muted mb-1">Current Role</label>
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                      {getUserRole()}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b border-white/10">
                    <span className="text-text-secondary">Administrator</span>
                    <span>
                      {isAdmin() ? (
                        <Icon name="CheckCircle" className="text-success" />
                      ) : (
                        <Icon name="XCircle" className="text-text-muted" />
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/10">
                    <span className="text-text-secondary">Writer</span>
                    <span>
                      {isWriter() ? (
                        <Icon name="CheckCircle" className="text-success" />
                      ) : (
                        <Icon name="XCircle" className="text-text-muted" />
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/10">
                    <span className="text-text-secondary">QA Reviewer</span>
                    <span>
                      {isQA() ? (
                        <Icon name="CheckCircle" className="text-success" />
                      ) : (
                        <Icon name="XCircle" className="text-text-muted" />
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-text-secondary">Client</span>
                    <span>
                      {isClient() ? (
                        <Icon name="CheckCircle" className="text-success" />
                      ) : (
                        <Icon name="XCircle" className="text-text-muted" />
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h2 className="text-xl font-semibold mb-4 text-text-primary">Role Description</h2>
                <div className="text-text-secondary">
                  {isAdmin() && (
                    <p>As an Administrator, you have full access to all features and can manage users, content, and system settings.</p>
                  )}
                  {isWriter() && (
                    <p>As a Writer, you can create and edit course content, but cannot modify system settings or manage users.</p>
                  )}
                  {isQA() && (
                    <p>As a QA Reviewer, you can review and approve content before it's published, ensuring quality standards are met.</p>
                  )}
                  {isClient() && (
                    <p>As a Client, you have access to all courses and can provide testimonials about your experience.</p>
                  )}
                  {!isAdmin() && !isWriter() && !isQA() && !isClient() && (
                    <p>As a Regular User, you can access courses you've enrolled in and manage your own profile.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
